/**
 * "Point your domain" — the step clients dread. Three honest routes: they own
 * one, we buy one, or they stay on the free address for now. The DNS records
 * come from lib/dns so they can never disagree with the dashboard's Custom
 * Domain page.
 *
 * The "I own one" route is a real connect (fnd-bmi.1): POST /api/domain/connect
 * creates the Cloudflare for SaaS custom hostname and returns the exact records
 * to add. When the owner has not enabled that yet the endpoint answers 501 and
 * this step degrades to the same records plus "we will do it by hand" — never a
 * button that pretends to have done something.
 *
 * Availability search is a real call (`/api/domain/check-availability`);
 * registration itself still goes through us, so that path ends in a message
 * rather than a fake checkout.
 */

import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Panel, PanelBody, PanelHeader, Tag } from '../../ui/kit';
import { ChoiceCard, CopyRow, TaskDone, TaskShell, TextInput } from '../TaskShell';
import { buildDnsRecords, normalizeDomainInput } from '../../../lib/dns';
import { customDomainService, customDomainErrorMessage } from '../../../services/customDomainService';
import type { CustomDomainState } from '../../../services/customDomainService';
import type { SetupStatus } from '../../../lib/setupFlow';
import type { TaskRecord } from '../../../lib/setupTasks';

const SUPPORT_EMAIL = 'hello@foundlio.com';

type Route = 'own' | 'buy' | 'subdomain';

interface Availability {
  domain: string;
  available: boolean;
  price?: number;
  registrar?: string;
  error?: string;
}

export default function DomainTask({
  status,
  record,
  onBack,
  onComplete,
}: {
  status: SetupStatus;
  record?: TaskRecord;
  onBack: () => void;
  onComplete: (data: Record<string, string>) => void;
}) {
  const [route, setRoute] = useState<Route>('own');
  const [ownDomain, setOwnDomain] = useState('');
  const [showRecords, setShowRecords] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  /** Null until we have asked the backend; `enabled: false` means "coming soon". */
  const [connectState, setConnectState] = useState<CustomDomainState | null>(null);

  const [buyTerm, setBuyTerm] = useState('');
  const [buyLoading, setBuyLoading] = useState(false);
  const [buyResult, setBuyResult] = useState<Availability | null>(null);

  // Prefer the records the backend actually provisioned for; fall back to the
  // shared builder so the panel is never empty while we are still asking.
  const records = useMemo(
    () =>
      connectState?.records?.length
        ? connectState.records
        : buildDnsRecords(ownDomain, connectState?.fallbackOrigin),
    [connectState?.records, connectState?.fallbackOrigin, ownDomain]
  );

  /**
   * Ask the backend to connect the domain, then reveal the records. Demo mode
   * promises no backend calls, so it skips straight to the preview records.
   */
  async function connectDomain() {
    const domain = normalizeDomainInput(ownDomain);
    if (!domain) return;
    setConnectError(null);

    if (status.isMock) {
      setShowRecords(true);
      return;
    }

    setConnecting(true);
    try {
      const state = await customDomainService.connect(domain);
      setConnectState(state);
      setShowRecords(true);
    } catch (error) {
      setConnectError(
        customDomainErrorMessage(error) ?? 'Could not start that one. Check the spelling and try again.'
      );
    } finally {
      setConnecting(false);
    }
  }

  if (record?.completedAt) {
    const chosen = record.data?.route;
    return (
      <TaskShell eyebrow="Step · domain" title="Domain sorted." onBack={onBack}>
        <TaskDone
          title={
            chosen === 'subdomain'
              ? "You're on your Foundlio address"
              : chosen === 'buy'
                ? "We'll register it for you"
                : 'Records added — we take it from here'
          }
          detail={
            chosen === 'subdomain'
              ? 'Your site works today, and you can move it to your own domain any time from your account.'
              : chosen === 'buy'
                ? 'We buy the domain, point it at your site and issue the certificate. Nothing else to do.'
                : 'DNS takes anywhere from a few minutes to a few hours to propagate. We watch it and issue the SSL certificate as soon as it lands.'
          }
          onBack={onBack}
          extra={
            record.data?.domain ? (
              <div className="font-mono text-[13px] text-zinc-600">{record.data.domain}</div>
            ) : undefined
          }
        />
      </TaskShell>
    );
  }

  async function search() {
    const term = normalizeDomainInput(buyTerm);
    if (!term) return;
    setBuyLoading(true);
    setBuyResult(null);

    // Demo mode promises no backend calls, and the availability endpoint is
    // public — so it would happily answer. Keep the promise.
    if (status.isMock) {
      window.setTimeout(() => {
        setBuyResult({ domain: term, available: !term.startsWith('taken'), price: 14 });
        setBuyLoading(false);
      }, 600);
      return;
    }
    try {
      const response = await axios.post('/api/domain/check-availability', { domain: term });
      setBuyResult(response.data?.data ?? response.data);
    } catch {
      setBuyResult({ domain: term, available: false, error: 'Could not check that one. Try again in a moment.' });
    } finally {
      setBuyLoading(false);
    }
  }

  const buyMailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    `Domain purchase ${status.orderRef}`
  )}&body=${encodeURIComponent(`Please register ${buyResult?.domain ?? buyTerm} for us.`)}`;

  return (
    <TaskShell
      eyebrow="Step · domain"
      title="Point your domain at your site."
      description="Your site is already live on a Foundlio address. This step puts it on the name customers will type."
      onBack={onBack}
    >
      <div className="space-y-3">
        <ChoiceCard
          title="I already own a domain"
          description="Add two records at your registrar. We verify and issue the certificate."
          selected={route === 'own'}
          onSelect={() => setRoute('own')}
        />
        <ChoiceCard
          title="Buy one for me"
          description="Tell us the name you want. We register it and wire it up — cost lands on your next invoice."
          selected={route === 'buy'}
          onSelect={() => setRoute('buy')}
        />
        <ChoiceCard
          title="Stay on my Foundlio address for now"
          description={`Keep ${status.previewUrl?.replace(/^https?:\/\//, '') ?? 'your free address'} and move later. Nothing breaks.`}
          selected={route === 'subdomain'}
          onSelect={() => setRoute('subdomain')}
        />
      </div>

      {route === 'own' && (
        <Panel>
          <PanelHeader title="Your domain" />
          <PanelBody className="space-y-4">
            <TextInput
              value={ownDomain}
              onChange={(event) => {
                setOwnDomain(event.target.value);
                setShowRecords(false);
              }}
              placeholder="valleyplumbingmadison.com"
              autoComplete="url"
            />

            {connectError && <p className="text-[13px] text-red-700">{connectError}</p>}

            {!showRecords ? (
              <button
                type="button"
                disabled={connecting || !normalizeDomainInput(ownDomain)}
                onClick={() => void connectDomain()}
                className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
              >
                {connecting ? 'Setting it up…' : 'Show me the records'}
              </button>
            ) : (
              <>
                {/* Honest about which half of this actually happened. */}
                {connectState && !connectState.enabled && (
                  <div className="rounded-lg border border-zinc-200 bg-paper-tint p-4">
                    <p className="text-sm leading-relaxed text-muted">
                      Self-serve domain connection is not switched on yet, so we will wire this up by
                      hand once you have added the records below — or send us the registrar login and
                      we will do the whole thing.
                    </p>
                  </div>
                )}
                <div className="rounded-lg border border-zinc-200 bg-paper-tint p-4">
                  <p className="text-sm leading-relaxed text-muted">
                    Sign in wherever you bought <span className="font-mono text-[13px] text-primary">{normalizeDomainInput(ownDomain)}</span> (GoDaddy,
                    Namecheap, Google Domains…), open its DNS settings, and add these. Delete any
                    existing <span className="font-mono text-[13px]">A</span> or{' '}
                    <span className="font-mono text-[13px]">CNAME</span> record on the same host first.
                  </p>
                </div>

                {records.map((dnsRecord) => (
                  <div key={`${dnsRecord.type}-${dnsRecord.host}`} className="rounded-lg border border-zinc-200 p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <Tag tone="neutral">{dnsRecord.type}</Tag>
                    </div>
                    <CopyRow label="Host" value={dnsRecord.host} />
                    <CopyRow label="Points to" value={dnsRecord.value} />
                    {/* Most registrars refuse a literal CNAME on the root and
                        hide the equivalent under ALIAS/ANAME — say so here. */}
                    {dnsRecord.note && (
                      <p className="mt-2 text-[12px] leading-relaxed text-zinc-500">{dnsRecord.note}</p>
                    )}
                  </div>
                ))}

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      onComplete({
                        route: 'own',
                        domain: normalizeDomainInput(ownDomain),
                        summary: `Added DNS records for ${normalizeDomainInput(ownDomain)}`,
                      })
                    }
                    className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    I&apos;ve added the records
                  </button>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`DNS help ${status.orderRef}`)}`}
                    className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500 transition-colors hover:text-primary"
                  >
                    Rather we did it? Send us the login →
                  </a>
                </div>
              </>
            )}
          </PanelBody>
        </Panel>
      )}

      {route === 'buy' && (
        <Panel>
          <PanelHeader title="Find a name" />
          <PanelBody className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <TextInput
                value={buyTerm}
                onChange={(event) => setBuyTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void search();
                }}
                placeholder="valleyplumbingmadison.com"
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => void search()}
                disabled={buyLoading || !buyTerm.trim()}
                className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
              >
                {buyLoading ? 'Checking…' : 'Check'}
              </button>
            </div>

            {buyResult && (
              <div className="rounded-lg border border-zinc-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="font-mono text-[13px] text-primary">{buyResult.domain}</span>
                  {buyResult.error ? (
                    <Tag tone="neutral">Unavailable to check</Tag>
                  ) : buyResult.available ? (
                    <Tag tone="live">Available{buyResult.price ? ` · $${buyResult.price}/yr` : ''}</Tag>
                  ) : (
                    <Tag tone="neutral">Already taken</Tag>
                  )}
                </div>
                {buyResult.error && <p className="mt-2 text-[13px] text-muted">{buyResult.error}</p>}
                {buyResult.available && (
                  <a
                    href={buyMailto}
                    onClick={() =>
                      onComplete({
                        route: 'buy',
                        domain: buyResult.domain,
                        summary: `Asked us to register ${buyResult.domain}`,
                      })
                    }
                    className="mt-4 inline-block rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    Buy it for me
                  </a>
                )}
              </div>
            )}
          </PanelBody>
        </Panel>
      )}

      {route === 'subdomain' && (
        <Panel>
          <PanelBody className="space-y-3">
            <p className="text-sm leading-relaxed text-muted">
              Perfectly fine. Your site stays exactly as it is, on{' '}
              <span className="font-mono text-[13px] text-primary">
                {status.previewUrl?.replace(/^https?:\/\//, '') ?? 'your Foundlio address'}
              </span>
              . When you want your own name, it is one step in your account and we move it without
              downtime.
            </p>
            <button
              type="button"
              onClick={() =>
                onComplete({ route: 'subdomain', summary: 'Staying on the Foundlio address for now' })
              }
              className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              Keep my Foundlio address
            </button>
          </PanelBody>
        </Panel>
      )}
    </TaskShell>
  );
}
